import React, { memo, useState, useEffect, useCallback, useMemo } from "react";
import { View, ScrollView, StyleSheet } from "react-native";
import {
  Checkbox,
  Box,
  Typography,
  FormControlLabel,
  Select,
  MenuItem,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useIsFocused } from "@react-navigation/native";
import { LinearGradient } from "expo-linear-gradient";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { translateText } from "../translations/translationService";
import MenuDocument from "../components/Info/ComponentMenuDocument.js";
import ImageDetailsComponent from "../components/Info/ComponentDetalles.js";
import { InfoComponent, ImageComponent } from "../components/FullComponent.js";
import { buttonsTop, ButtonList } from "../components/Common/ComponentButtons.js";
import PasswordPrompt from "../components/Common/ComponentPasswordPrompt";
import AppButton from "../components/Common/ui/AppButton";
import AppDialog from "../components/Common/ui/AppDialog";
import AppSelect from "../components/Common/ui/AppSelect";
import AppTextField from "../components/Common/ui/AppTextField";
import ComponentTiemposProcesosCalidad from "../components/Info/ComponentTiemposProcesosCalidad.js";
import { updateInfoData, setTotalCantidad } from "../redux/reducers.js";
import {
  SCREEN_IDS,
  backgroundColor,
  LISTA_INCIDENCIAS,
  typeConnect,
} from "../constants/Constants.js";
import { insertLineaCalidad } from "../services/ConnectDataBase.js";
import { formatDate } from "../services/Functions.js";
import { INFO_SCREEN_COLORS } from "../style/colors.js";
import { notify } from "../services/ui/notify";

const PROCESOS = [
  { label: "REPASO", tipoReal: "REPASO" },
  { label: "ENGASTADO", tipoReal: "PIEDRAS" },
  { label: "PULIDO", tipoReal: "PULIDO" },
  { label: "BOMBOS", tipoReal: "BOMBOS" },
  { label: "GRABADO", tipoReal: "GRABADO" },
  { label: "BAÑOS", tipoReal: "BAÑOS" },
  { label: "FORNITURAS", tipoReal: "FORNITURA" },
];

const PESO_VARIACIONES = {
  NORMAL: 0.1,
  MODERADA: 0.15,
  MODERADA_MIN: 10,
  MODERADA_MAX: 15,
  SIGNIFICATIVA_MIN: 16,
};

const InfoScreen = ({ navigation, route }) => {
  const incidencia = route.params?.incidencia ?? false;
  const dispatch = useDispatch();
  const isFocused = useIsFocused();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    cvdNumber: "",
    lgdNumber: "",
    cantidadPiezas: "",
    pesoPieza: "",
    cantidadIncidencia: "",
    comentario: "",
    selectedIncidencia: LISTA_INCIDENCIAS[0].codigo,
  });

  const [checkboxes, setCheckboxes] = useState({
    cvdChecked: false,
    lgdChecked: false,
    incidenciaChecked: false,
  });

  const [passwordState, setPasswordState] = useState({
    visible: false,
    message: "",
    correctPasswords: null,
    callback: null,
  });

  const equipo = useSelector((state) => state.user.team);
  const usuario = useSelector((state) => state.user.name);
  const lang = useSelector((state) => state.lang.value);
  const infoData = useSelector((state) => state.infoData.infoData || {});
  const eFabrica = useSelector((state) => state.eFabrica);
  const authQualityUsers = useSelector((state) => state.authQualityUsers);
  const nivelAcceso = useSelector((state) => state.user.nivel_acceso);

  const piedras = useMemo(
    () => eFabrica.filter((item) => item.tipo === "PIEDRAS"),
    [eFabrica]
  );

  const piedrasCodigos = useMemo(
    () => piedras.map((item) => item.codigo),
    [piedras]
  );

  const quantities = useMemo(
    () => ({
      total: parseFloat(infoData.cantidad || 0),
      finished: parseFloat(infoData.cantidad_terminada || 0),
      pending:
        parseFloat(infoData.cantidad || 0) -
        parseFloat(infoData.cantidad_terminada || 0),
    }),
    [infoData.cantidad, infoData.cantidad_terminada]
  );

  const piedrasValidation = useMemo(() => {
    const startsWithX = piedras.some((item) =>
      item.codigo.toLowerCase().startsWith("x")
    );

    const hasBrCodeWithDigitsGreaterThan10 = piedras.some((item) => {
      const code = item.codigo.toLowerCase();
      if (code.startsWith("br") && code.length >= 4) {
        const thirdDigit = code[2];
        const fourthDigit = code[3];
        if (!isNaN(thirdDigit) && !isNaN(fourthDigit)) {
          const value = parseInt(thirdDigit + fourthDigit, 10);
          return value > 10;
        }
      }
      return false;
    });

    return { startsWithX, hasBrCodeWithDigitsGreaterThan10 };
  }, [piedras]);

  const calidadCompleta = useMemo(() => {
    if (!infoData.tiempos || !Array.isArray(infoData.tiempos)) return false;

    return (
      infoData.tiempos.some(
        (tiempo) =>
          tiempo.team === "Control de calidad" && tiempo.tipo === "Term."
      ) &&
      quantities.total === parseFloat(infoData.cantidad_fabricada) &&
      quantities.total === quantities.finished
    );
  }, [infoData.tiempos, infoData.cantidad_fabricada, quantities]);

  const formattedDate = useMemo(
    () => formatDate(infoData.fechaEntrega),
    [infoData.fechaEntrega]
  );

  const updateFormData = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updateCheckboxes = useCallback((field, value) => {
    setCheckboxes((prev) => ({ ...prev, [field]: value }));
  }, []);

  const showToast = useCallback((message, type = "info") => {
    notify[type]?.(message);
  }, []);

  const obtenerContrasenasValidas = useCallback(
    (maximoPermitido) => {
      return authQualityUsers
        .filter(
          (usuario) => parseFloat(usuario.hasta_porcentaje) >= maximoPermitido
        )
        .map((usuario) => usuario.password_calidad);
    },
    [authQualityUsers]
  );

  const validarCampos = useCallback(() => {
    const piezaTerminada = infoData.tiempos?.some(
      (tiempo) => tiempo.tipo === "Term." && quantities.pending === 0
    );

    if (piezaTerminada) {
      showToast("Esta pieza ya tiene calidad y peso final.", "error");
      return false;
    }

    if (
      piedrasValidation.hasBrCodeWithDigitsGreaterThan10 &&
      (!checkboxes.cvdChecked || !formData.cvdNumber.trim())
    ) {
      showToast("El campo Nº CVD es obligatorio para esta pieza.", "warn");
      return false;
    }

    if (
      piedrasValidation.startsWithX &&
      (!checkboxes.lgdChecked || !formData.lgdNumber.trim())
    ) {
      showToast("El campo Nº LGD es obligatorio para esta pieza.", "warn");
      return false;
    }

    if (!formData.pesoPieza.trim() && !checkboxes.incidenciaChecked) {
      showToast("El campo Peso es obligatorio.", "warn");
      return false;
    }

    if (checkboxes.incidenciaChecked && !formData.cantidadIncidencia.trim()) {
      showToast("El campo Cantidad incidencia es obligatorio.", "warn");
      return false;
    }

    if (
      parseFloat(formData.cantidadIncidencia) >
      parseFloat(formData.cantidadPiezas)
    ) {
      showToast(
        "El campo Cantidad incidencia no puede ser mayor que la cantidad de piezas.",
        "error"
      );
      return false;
    }

    return true;
  }, [
    infoData.tiempos,
    quantities.pending,
    piedrasValidation,
    checkboxes,
    formData,
    showToast,
  ]);

  const calcularPesoYVariacion = useCallback(() => {
    const pesoEstimado =
      parseFloat(formData.cantidadPiezas || 0) *
      parseFloat(infoData.pesobruto || 0);
    const pesoPiezaFloat = parseFloat(formData.pesoPieza);
    const variacionPorcentaje =
      pesoEstimado === 0
        ? 0
        : ((pesoPiezaFloat - pesoEstimado) / pesoEstimado) * 100;

    return { pesoEstimado, variacionPorcentaje, pesoPiezaFloat };
  }, [formData.cantidadPiezas, formData.pesoPieza, infoData.pesobruto]);

  const handlePasswordPrompt = useCallback(
    (message, correctPasswords, callback) => {
      setPasswordState({
        visible: true,
        message,
        correctPasswords,
        callback,
      });
    },
    []
  );

  const forniturasValidation = useMemo(() => {
    const forniturasRequeridas = eFabrica.filter(
      (item) => item.tipo === "FORNITURAS"
    );
    const tieneFornituras = forniturasRequeridas.length > 0;

    const tieneProcesoFornitura = infoData.tiempos?.some((tiempo) =>
      tiempo.tipo?.toLowerCase().trim().startsWith("fornitura")
    );

    return {
      tieneFornituras,
      tieneProcesoFornitura,
      forniturasRequeridas,
    };
  }, [eFabrica, infoData.tiempos]);

  const handlePasswordSubmit = useCallback(
    (password) => {
      const { correctPasswords, callback } = passwordState;

      try {
        if (correctPasswords.includes(password)) {
          const responsable = authQualityUsers.find(
            (user) => user.password_calidad === password
          )?.name;

          if (!responsable) {
            const errorCode = "E001_RESPONSABLE_NO_ENCONTRADO";
            console.error(
              `[${errorCode}] Contraseña válida pero no se encontró responsable.`
            );
            showToast(
              `Error: No se pudo identificar al responsable. Código: ${errorCode}`,
              "error"
            );
            return;
          }

          setPasswordState((prev) => ({ ...prev, visible: false }));

          if (callback && typeof callback === "function") {
            try {
              callback(responsable);
            } catch (err) {
              const errorCode = "E002_CALLBACK_FALLO";
              console.error(
                `[${errorCode}] Error en el callback de contraseña:`,
                err
              );
              showToast(
                `Error al continuar con el proceso. Código: ${errorCode}`,
                "error"
              );
            }
          } else {
            const errorCode = "E003_CALLBACK_NO_DEFINIDO";
            console.warn(
              `[${errorCode}] No se definió un callback válido en passwordState.`
            );
            showToast(
              `Error interno: no se pudo continuar. Código: ${errorCode}`,
              "error"
            );
          }
        } else {
          showToast("Contraseña incorrecta.", "error");
        }
      } catch (error) {
        const errorCode = "E004_EXCEPCION_GENERAL";
        console.error(
          `[${errorCode}] Excepción inesperada en handlePasswordSubmit:`,
          error
        );
        showToast(
          `Error inesperado en el sistema. Código: ${errorCode}`,
          "error"
        );
      }
    },
    [passwordState, authQualityUsers, showToast]
  );

  const insertCalidad = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (!validarCampos()) {
        setIsSubmitting(false);
        return;
      }

      if (!infoData) {
        const errorCode = "E100_INFODATA_NULL";
        console.error(`[${errorCode}] infoData es null o undefined`);
        showToast(
          `Error: Datos de la orden no disponibles. Código: ${errorCode}`,
          "error"
        );
        setIsSubmitting(false);
        return;
      }

      const tzoffset = new Date().getTimezoneOffset() * 60000;
      const today = new Date(Date.now() - tzoffset).toISOString().slice(0, -1);

      const finalData = {
        cvdNumber: checkboxes.cvdChecked ? formData.cvdNumber : "",
        lgdNumber: checkboxes.lgdChecked ? formData.lgdNumber : "",
        cantidadIncidencia: checkboxes.incidenciaChecked
          ? formData.cantidadIncidencia
          : "",
        selectedIncidencia: checkboxes.incidenciaChecked
          ? formData.selectedIncidencia
          : "",
      };

      const insertCalidadData = async (responsable = null) => {
        try {
          const { pesoEstimado, variacionPorcentaje } =
            calcularPesoYVariacion();

          // Solo enviar variación si es ≥ 10% 
          const variacionParaEnviar = Math.abs(variacionPorcentaje) >= 10 ? variacionPorcentaje : null;

          const result = await insertLineaCalidad(
            SCREEN_IDS.INFO,
            typeConnect[1],
            infoData.PO,
            infoData.MO,
            usuario,
            equipo,
            today,
            "TIME_WORK",
            checkboxes.cvdChecked,
            finalData.cvdNumber,
            checkboxes.lgdChecked,
            formData.cantidadPiezas,
            formData.pesoPieza,
            checkboxes.incidenciaChecked,
            finalData.cantidadIncidencia,
            finalData.selectedIncidencia,
            formData.comentario,
            finalData.lgdNumber,
            responsable,
            null,
            pesoEstimado,
            variacionParaEnviar
          );


          if (result !== false) {
            showToast("Calidad añadida correctamente.", "success");
            setIsModalVisible(false);
            
            // Actualizar el estado local para reflejar que ya tiene calidad
            if (infoData && infoData.tiempos) {
              const updatedInfoData = {
                ...infoData,
                tiempos: [
                  ...infoData.tiempos,
                  {
                    team: "Control de calidad",
                    tipo: "CALIDAD",
                    usuario: usuario,
                    fecha: today
                  }
                ]
              };
              dispatch(updateInfoData(updatedInfoData));
            }
          } else {
            const errorCode = "E101_INSERCION_FALLIDA";
            console.error(
              `[${errorCode}] insertLineaCalidad devolvió resultado no válido`,
              result
            );
            showToast(
              `Error: No se pudo guardar la calidad. Código: ${errorCode}`,
              "error"
            );
          }
        } catch (err) {
          const errorCode = "E102_INSERCION_EXCEPCION";
          console.error(
            `[${errorCode}] Excepción durante insertLineaCalidad:`,
            err
          );
          showToast(
            `Error al guardar la calidad. Código: ${errorCode}`,
            "error"
          );
        }
      };

      const continuarConValidacionPeso = async (
        responsableFornituras = null
      ) => {
        try {
          if (formData.pesoPieza) {
            const { pesoEstimado, variacionPorcentaje, pesoPiezaFloat } =
              calcularPesoYVariacion();
            const variacion = PESO_VARIACIONES.NORMAL * pesoEstimado;
            const variacionMayor = PESO_VARIACIONES.MODERADA * pesoEstimado;

            const pesoMinimo = pesoEstimado - variacion;
            const pesoMaximo = pesoEstimado + variacion;
            const pesoMinimoMayor = pesoEstimado - variacionMayor;
            const pesoMaximoMayor = pesoEstimado + variacionMayor;

            if (pesoPiezaFloat >= pesoMinimo && pesoPiezaFloat <= pesoMaximo) {
              await insertCalidadData(responsableFornituras);
            } else if (
              (pesoPiezaFloat >= pesoMinimoMayor &&
                pesoPiezaFloat < pesoMinimo) ||
              (pesoPiezaFloat <= pesoMaximoMayor && pesoPiezaFloat > pesoMaximo)
            ) {
              const contrasenasValidas = obtenerContrasenasValidas(
                PESO_VARIACIONES.MODERADA_MAX
              );
              handlePasswordPrompt(
                `El peso tiene una variación moderada entre 10% y 15% - (${variacionPorcentaje.toFixed(
                  2
                )}%). Introduzca la contraseña para continuar:`,
                contrasenasValidas,
                (responsablePeso) => {
                  try {
                    insertCalidadData(responsablePeso || responsableFornituras);
                  } catch (err) {
                    const errorCode = "E103_CALLBACK_PESO_MODERADO";
                    console.error(
                      `[${errorCode}] Error en el callback de validación moderada:`,
                      err
                    );
                    showToast(
                      `Error al validar el peso. Código: ${errorCode}`,
                      "error"
                    );
                  }
                }
              );
            } else {
              const contrasenasValidas = obtenerContrasenasValidas(
                PESO_VARIACIONES.SIGNIFICATIVA_MIN
              );
              handlePasswordPrompt(
                `El peso tiene una variación significativa mayor al 15% - (${variacionPorcentaje.toFixed(
                  2
                )}%). Introduzca la contraseña para continuar:`,
                contrasenasValidas,
                (responsablePeso) => {
                  try {
                    insertCalidadData(responsablePeso || responsableFornituras);
                  } catch (err) {
                    const errorCode = "E104_CALLBACK_PESO_SIGNIFICATIVO";
                    console.error(
                      `[${errorCode}] Error en el callback de validación significativa:`,
                      err
                    );
                    showToast(
                      `Error al validar el peso. Código: ${errorCode}`,
                      "error"
                    );
                  }
                }
              );
            }
          } else {
            await insertCalidadData(responsableFornituras);
          }
        } catch (err) {
          const errorCode = "E105_CONTINUAR_VALIDACION_PESO";
          console.error(
            `[${errorCode}] Error inesperado en continuarConValidacionPeso:`,
            err
          );
          showToast(
            `Error en validación de peso. Código: ${errorCode}`,
            "error"
          );
        }
      };

      if (
        forniturasValidation?.tieneFornituras &&
        !forniturasValidation?.tieneProcesoFornitura
      ) {
        const contrasenasValidas = obtenerContrasenasValidas(15);
        const forniturasListado =
          forniturasValidation.forniturasRequeridas
            ?.map((f) => f.codigo)
            .join(", ") || "N/A";

        handlePasswordPrompt(
          `Esta pieza tiene fornituras (${forniturasListado}) que no tienen peso añadido. Introduzca la contraseña para continuar:`,
          contrasenasValidas,
          (responsable) => {
            try {
              continuarConValidacionPeso(responsable);
            } catch (e) {
              const errorCode = "E106_CALLBACK_FORNITURAS";
              console.error(
                `[${errorCode}] Error interno en el callback de fornituras:`,
                e
              );
              showToast(
                `Error validando fornituras. Código: ${errorCode}`,
                "error"
              );
            }
          }
        );
        return;
      }

      await continuarConValidacionPeso();
    } catch (error) {
      const errorCode = "E999_INSERT_CALIDAD_GENERAL";
      console.error(`[${errorCode}] Error general en insertCalidad:`, error);
      showToast(`Error inesperado. Código: ${errorCode}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    validarCampos,
    infoData,
    checkboxes,
    formData,
    calcularPesoYVariacion,
    obtenerContrasenasValidas,
    handlePasswordPrompt,
    forniturasValidation,
    usuario,
    equipo,
    showToast,
  ]);

  useEffect(() => {
    if (incidencia && infoData) {
      navigation.navigate("Incidences quality", { infoData });
    }
  }, [incidencia, infoData, navigation]);

  useEffect(() => {
    try {
      dispatch(setTotalCantidad(quantities.pending));
      if (isFocused && infoData) {
        dispatch(updateInfoData(infoData));
        setFormData((prev) => ({
          ...prev,
          cantidadPiezas: isNaN(quantities.pending)
            ? ""
            : quantities.pending.toFixed(1),
        }));
      }
    } catch (error) {
      console.error("Error en useEffect principal:", error);
      showToast("Error inesperado al actualizar datos.", "error");
    }
  }, [dispatch, isFocused, infoData, quantities.pending]);

  const ProcessTable = memo(() => (
    <TableContainer component={Paper} variant='outlined'>
      <Table size='small' aria-label='tabla pasos'>
        <TableBody>
          <TableRow sx={{ backgroundColor: INFO_SCREEN_COLORS.BACKGROUND_TABLE_HEADER }}>
            {PROCESOS.map(({ label, tipoReal }) => {
              let existe;
              if (tipoReal === "FORNITURA") {
                existe = infoData.tiempos?.some((p) =>
                  p.tipo?.toLowerCase().trim().startsWith("fornitura")
                );
              } else {
                existe = infoData.tiempos?.some((p) => p.tipo === tipoReal);
              }
              return (
                <TableCell
                  key={label}
                  align='center'
                  sx={{
                    verticalAlign: "middle",
                    fontWeight: "bold",
                    py: 0.75,
                    px: 0.5,
                  }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                      flexWrap: "nowrap",
                    }}>
                    <Typography component='span' fontSize='0.75rem' fontWeight='bold'>
                      {translateText(label, lang)}
                    </Typography>
                    {existe ? (
                      <CheckCircleOutlineIcon color='success' fontSize='small' sx={{ fontSize: 18 }} />
                    ) : (
                      <CancelOutlinedIcon color='error' fontSize='small' sx={{ fontSize: 18 }} />
                    )}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  ));

  const InfoSection = memo(({ title, data }) => (
    <Paper variant='outlined' sx={{ p: 1, width: "100%" }}>
      <Grid container spacing={0.25}>
        {Object.entries(data).map(([key, value]) => (
          <React.Fragment key={key}>
            <Grid item xs={6}>
              <Typography fontWeight='bold' fontSize='0.85rem'>
                {key}:
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography color='text.secondary' fontSize='0.85rem'>
                {value}
              </Typography>
            </Grid>
          </React.Fragment>
        ))}
      </Grid>
    </Paper>
  ));

  return (
    <>
      <LinearGradient
        colors={backgroundColor()}
        style={styles.backgroundOptionDos}>
        <ScrollView contentContainerStyle={styles.scrollViewContent}>
          <View style={styles.container}>
            <View style={styles.row}>
              <View style={styles.column}>
                <InfoComponent data={infoData} calidad={calidadCompleta} />
              </View>
              <View style={styles.column}>
                <ImageComponent data={infoData} />
              </View>
            </View>
            <View style={styles.buttonsRow}>
              <ButtonList
                buttons={buttonsTop({
                  infoData,
                  navigation,
                  lang,
                  screenName: "infoScreen",
                  equipo,
                  setIsModalVisible,
                  nivelAcceso,
                })}
              />
            </View>
          </View>
        </ScrollView>

        <AppDialog
          open={isModalVisible}
          onClose={() => setIsModalVisible(false)}
          maxWidth='lg'
          title={translateText("Control de Calidad", lang)}
          headerActions={
            <Box display='flex' alignItems='center' gap={2}>
              <MenuDocument
                data={infoData}
                sx={{ "& .MuiButton-root": { fontSize: 0 } }}
              />
              <ImageDetailsComponent
                imageUrl={infoData.imgcalidad}
                mo={infoData}
                hideText={true}
              />
            </Box>
          }
          actions={
            <>
              <AppButton
                onClick={() => setIsModalVisible(false)}
                variantType='secondary'>
                {translateText("Cancelar", lang)}
              </AppButton>
              <AppButton
                onClick={insertCalidad}
                disabled={isSubmitting}>
                {translateText("Aceptar", lang)}
              </AppButton>
            </>
          }>
            <Grid container spacing={1}>
              <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                <InfoSection
                  title='Datos Generales'
                  data={{
                    Referencia: infoData.referencia,
                    Piedras: piedrasCodigos.join(", "),
                    PO: infoData.PO,
                    MO: infoData.MO,
                    Unidades: quantities.total.toFixed(1),
                    Fabricada: quantities.finished.toFixed(1),
                    Pendiente: quantities.pending.toFixed(1),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                <InfoSection
                  title='Datos Cliente'
                  data={{
                    "Fecha de entrega": formattedDate,
                    "Tipo orden": infoData.tipo_orden,
                    Stamping: infoData.stamping,
                    "Clasif. Cliente": infoData.clasificacionCliente,
                    "Peso Teórico": (
                      quantities.total * parseFloat(infoData.pesobruto || 0)
                    ).toFixed(2),
                    "Peso Uni": parseFloat(infoData.pesobruto || 0).toFixed(2),
                    "Peso Restante": (
                      quantities.pending * parseFloat(infoData.pesobruto || 0)
                    ).toFixed(2),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                <Paper
                  variant='outlined'
                  sx={{
                    p: 1,
                    textAlign: "center",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: { xs: 180, md: "auto" },
                  }}>
                  <Box
                    component='img'
                    src={infoData.image}
                    alt='Producto'
                    sx={{
                      maxWidth: "100%",
                      maxHeight: 170,
                      objectFit: "contain",
                      borderRadius: 2,
                    }}
                  />
                </Paper>
              </Grid>
            </Grid>

            <Box my={2}>
              <Divider />
            </Box>

            <Grid container spacing={1} mt={1}>
              <Grid item xs={4}>
                <Box display='flex' flexDirection='column' gap={2}>
                  <Box display='flex' alignItems='center' minHeight={52}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checkboxes.cvdChecked}
                          onChange={(e) =>
                            updateCheckboxes("cvdChecked", e.target.checked)
                          }
                          disabled={
                            !piedrasValidation.hasBrCodeWithDigitsGreaterThan10
                          }
                        />
                      }
                      label='CVD'
                      sx={{ mr: 2 }}
                    />
                    <AppTextField
                      label='Nº CVD'
                      value={formData.cvdNumber}
                      onChange={(e) =>
                        updateFormData("cvdNumber", e.target.value)
                      }
                      disabled={
                        !piedrasValidation.hasBrCodeWithDigitsGreaterThan10 ||
                        !checkboxes.cvdChecked
                      }
                    />
                  </Box>

                  <Box display='flex' alignItems='center' minHeight={52}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checkboxes.lgdChecked}
                          onChange={(e) =>
                            updateCheckboxes("lgdChecked", e.target.checked)
                          }
                          disabled={!piedrasValidation.startsWithX}
                        />
                      }
                      label='LGD'
                      sx={{ mr: 2 }}
                    />
                    <AppTextField
                      label='Nº LGD'
                      value={formData.lgdNumber}
                      onChange={(e) =>
                        updateFormData("lgdNumber", e.target.value)
                      }
                      disabled={
                        !piedrasValidation.startsWithX || !checkboxes.lgdChecked
                      }
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={4}>
                <Box display='flex' flexDirection='column' gap={2}>
                  <Box display='flex' alignItems='center' minHeight={52}>
                    <AppTextField
                      label={translateText("Cantidad de piezas", lang)}
                      value={formData.cantidadPiezas}
                      onChange={(e) =>
                        updateFormData("cantidadPiezas", e.target.value)
                      }
                    />
                  </Box>
                  <Box display='flex' alignItems='center' minHeight={52}>
                    <AppTextField
                      label={translateText("Peso", lang)}
                      value={formData.pesoPieza}
                      onChange={(e) =>
                        updateFormData("pesoPieza", e.target.value)
                      }
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={4}>
                <Box display='flex' flexDirection='column' gap={2}>
                  <Box display='flex' alignItems='center' minHeight={52}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checkboxes.incidenciaChecked}
                          onChange={(e) =>
                            updateCheckboxes(
                              "incidenciaChecked",
                              e.target.checked
                            )
                          }
                        />
                      }
                      label={translateText("Incidencia", lang)}
                      sx={{ mr: 2 }}
                    />
                    <AppTextField
                      label={translateText("Cantidad incidencia", lang)}
                      value={formData.cantidadIncidencia}
                      onChange={(e) =>
                        updateFormData("cantidadIncidencia", e.target.value)
                      }
                      disabled={!checkboxes.incidenciaChecked}
                    />
                  </Box>

                  <Box display='flex' alignItems='center' minHeight={52}>
                  <AppSelect
                    value={formData.selectedIncidencia}
                    onChange={(e) =>
                      updateFormData("selectedIncidencia", e.target.value)
                    }
                    displayEmpty>
                    {LISTA_INCIDENCIAS.map((incidencia) => (
                      <MenuItem
                        key={incidencia.codigo}
                        value={incidencia.codigo}>
                        {translateText(incidencia.descripcion, lang)}
                      </MenuItem>
                    ))}
                  </AppSelect>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Box my={2}>
              <Divider />
            </Box>

            <Grid container spacing={1}>
              <Grid item xs={12}>
                <ComponentTiemposProcesosCalidad
                  referencia={infoData.referencia}
                  lang={lang}
                />
              </Grid>
            </Grid>

            <Box my={2}>
              <Divider />
            </Box>

            <ProcessTable />

            <AppTextField
              label={translateText("Comentario", lang)}
              value={formData.comentario}
              onChange={(e) => updateFormData("comentario", e.target.value)}
              multiline
              rows={2}
              margin='normal'
            />
        </AppDialog>

        <PasswordPrompt
          visible={passwordState.visible}
          message={passwordState.message}
          onSubmit={handlePasswordSubmit}
          onCancel={() =>
            setPasswordState((prev) => ({ ...prev, visible: false }))
          }
        />
      </LinearGradient>
    </>
  );
};

const styles = StyleSheet.create({
  backgroundOptionDos: {
    flex: 1,
  },
  scrollViewContent: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
    padding: 10,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "stretch",
  },
  column: {
    flex: 1,
    padding: 5,
  },
  buttonsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "flex-start",
  },
});

export default memo(InfoScreen);
